import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subscription } from "rxjs";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { FilterValues } from "src/components/shared-components/class/general-classes/filter";
import { HistoryAlarm } from "src/model/class/general classes/history-alarm";
import {
  Column,
  ItemHistory,
  TableInfo,
  ValueToChange,
} from "src/components/shared-components/class/general-classes/item-history";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { FiltersType } from "src/components/shared-components/enum/enumFilters";
import {
  BIGLogicLogState,
  BIGLogicTypeAlarm,
} from "src/components/shared-components/enum/enumLogic";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import {
  ColumnTableType,
  HideShowColumn,
} from "src/components/shared-components/enum/enumTableHistory";
import { iGenericTable } from "src/model/interface/iGeneric-table";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { EnvironmentService } from "src/service/environment.service";
import { LogicsService } from "src/service/logics.service";
import { NetworkService } from "src/service/network.service";
import { TranslationService } from "src/service/translation.service";
import { FilterAlarmsHistoryComponent } from "src/components/filter components/filter-alarms-history/filter-alarms-history.component";
import { DateFormatterFilterService } from "src/service/date-formatter-filter.service";
import { Store } from "@ngrx/store";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { listEnvironment } from "src/state/selectors/environment.selectors";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { Environment } from "src/components/shared-components/class/general-classes/environment";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Component({
    selector: "app-alarms-history-list",
    templateUrl: "./alarms-history-list.component.html",
    styleUrls: ["./alarms-history-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AlarmsHistoryListComponent
  implements OnInit, OnDestroy, iTranslation, iGenericTable
{
  @Input() isUserInPage: boolean = true;
  private _logContext: string = "alarms-history-list";

  private _networkServiceSubscription: Subscription = null;
  private _selectedBuildingSubscription: Subscription = null;
  private _listEnvironmentSubscription: Subscription = null;
  private _idAreaLocal: number = -1;
  private _environments: Environment[] = [];

  //#region translation
  private _translateSubscription: Subscription = null;
  private _dictionary: string = "dictionary.";
  private _alarmType: string = "alarmType";
  private _date: string = "date";
  private _day: string = "day";
  private _description: string = "description";
  private _duration: string = "duration";
  private _environment: string = "environment";
  private _resetDate: string = "resetDate";
  private _resetDay: string = "resetDay";
  private _resetNotes: string = "resetNotes";
  private _resetUserName: string = "resetUserName";
  private _solutionDate: string = "solutionDate";
  private _solutionDay: string = "solutionDay";
  private _solveLaterUserName: string = "solveLaterUserName";
  private _solutionNotes: string = "solutionNotes";
  private _userNameSolution: string = "userNameSolution";
  private _workingOverNotes: string = "workingOverNotes";
  private _lblErrorDate: string = "startDateBiggerEndDate";
  //#endregion

  private _alarmPeriodHours: string = "alarmPeriodHours";
  private _idTypeAlarmImage: string = "idTypeAlarmImage";

  //#region filter names
  private _fromDay: string = "fromDay";
  private _fromDaySolution: string = "fromDaySolution";
  private _logicLogState: string = "logicLogState";
  private _toDay: string = "toDay";
  private _toDaySolution: string = "toDaySolution";
  //#endregion

  //#region css class
  private _genericTableLargeCell: string = "generic-table-large-cell";
  private _danger: string = "generic-table-danger";
  private _primary: string = "generic-table-primary";
  private _resetCss: string = "generic-table-reset";
  private _solutionCss: string = "generic-table-solution";
  private _solveLaterCss: string = "generic-table-solve-later";
  private _warning: string = "generic-table-warning";
  //#endregion

  private _defaultDate: string = "01/01/0001";
  private _currentQuery: string = "";

  private _historyAlarms: Array<HistoryAlarm> = new Array<HistoryAlarm>();
  private _idAll: number = -1;
  private _isLoadingAlarmsHistory: boolean = false;
  private _alarmsHistoryPageScroll: number = 0;
  private _numElementInScroll: number = 100;
  private _currentOffset: number = 0;
  private _totalAlarmsHistoryCount: number = 0;
  private _remainingAccessHistoryItems: string = "";

  public get remainingAccessHistoryItems(): string {
    return this._remainingAccessHistoryItems;
  }

  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public tableInfo: TableInfo = null;
  public selected: Array<any> = new Array<any>();
  /** Filter panel unfolded (phones/tablets only: on wide screens it is always visible) */
  public isToggleFilterAlarmsHistory: boolean = false;
  /** Filters of the last search, summarised on the folded panel (phones) */
  public appliedFilters: { fromDay: string; toDay: string; environment: string; name: string } = null;
  public isScrollVisible: boolean = false;
  public isLoading: boolean = false;
  public alarmsHistoryScroll: Array<any> = [];
  public alarmsHistoryFiltered: Array<any> = [];
  public isAlarmsHistoryEmpty: boolean = false;
  public toggledOpenItemIds: number[] = [];

  @ViewChild(FilterAlarmsHistoryComponent)
  filterAlarmsHistoryComponent: FilterAlarmsHistoryComponent;

  constructor(
    private _logicService: LogicsService,
    private _translateService: TranslateService,
    private _loaderService: LoaderService,
    private _alertService: AlertService,
    private _platform: Platform,
    private _networkService: NetworkService,
    private _translationService: TranslationService,
    private _dateFormatterService: DateFormatterFilterService,
    private _store: Store,
    private _environmentService: EnvironmentService,
    private _loggerService: LoggerService,
  ) {}

  ngOnInit(): void {
    this.tableInfo = StaticClass.createTableInfo(
      "",
      true,
      null,
      this.createArrayColumns(),
      this._platform.is("android") || this._platform.is("ios"),
      false,
    );
    this.tableInfo.IsListInitialized = false;

    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();

    this.subscribeBuildingChange();
    this.initFilterFields();
  }

  ngOnDestroy(): void {
    this._translateSubscription?.unsubscribe();
    this._networkServiceSubscription?.unsubscribe();
    this._selectedBuildingSubscription?.unsubscribe();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([this._dictionary + this._lblErrorDate])
      .subscribe((translation) => {
        this._lblErrorDate = translation[this._dictionary + this._lblErrorDate];
        moment.locale(this._translateService.getCurrentLang());
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
        moment.locale(this._translateService.getCurrentLang());

        this.createItemHistoryDesktopList();
      },
    );
  }
  //#endregion

  /**
   *  Handle Filter Request, call api with the queryString defined by alarms history filtered values
   * @param query : The formated query string extracted from the Map<string,string> filtered values
   */
  public async onRequestAlarmHistoryFilter(
    query: string,
    skipLoader: boolean = false,
    isInfiniteScroll: boolean = false,
  ): Promise<void> {
    this.tableInfo.ShowErrorContent = false;
    try {
      if (!skipLoader) {
        await this._loaderService.presentLoaderWaiting();
      }
      this._currentQuery = query;
      const response =
        await this._logicService.GetAlarmsHistoryByEnvironment("?" + query);
      
      const listAlarmsHistory = response.items || (Array.isArray(response) ? response : []);
      this._totalAlarmsHistoryCount = response.totalCount || response.total || response.count || listAlarmsHistory.length;

      if (isInfiniteScroll) {
        this._historyAlarms = [
          ...this._historyAlarms,
          ...(listAlarmsHistory ? listAlarmsHistory : []),
        ];
      } else {
        this._historyAlarms = listAlarmsHistory ? listAlarmsHistory : [];
      }

      this.createItemHistoryDesktopList();

      this.tableInfo.IsListInitialized = true;
    } catch (exception) {
      await this._loaderService.dismissLoaderWaiting();
      await this.handlePeculiarApiResponseError(exception);
    } finally {
      if (!skipLoader) {
        await this._loaderService.dismissLoaderWaiting();
      }
    }
  }

  /**
   * Creates the array containing the alarms history items to show in the App view
   */
  public createItemHistoryList(): void {
    if (!Array.isArray(this._historyAlarms)) {
      this._historyAlarms = [];
    }
    this.tableInfo.List = this._historyAlarms.map((historyAlarm) => {
      let newAlarmsHistoryItem: ItemHistory = new ItemHistory();
      newAlarmsHistoryItem.Id = historyAlarm.id;
      newAlarmsHistoryItem.IconInfo = StaticClass.createIconInfo(
        StaticClass.GetTypeAlarm(historyAlarm.idTypeAlarmImage),
        this.getColorIcon(historyAlarm),
      );
      newAlarmsHistoryItem.Title = StaticClass.createTextInfo(
        historyAlarm.description,
        "",
      );
      newAlarmsHistoryItem.Subtitles = [];
      newAlarmsHistoryItem.Subtitles.push(
        StaticClass.createSubtitle(
          this.uniteDateAndTime(historyAlarm.day, historyAlarm.time),
          "",
          this._warning,
          this._danger,
        ),
      );
      if (historyAlarm.solutionDay != this._defaultDate) {
        newAlarmsHistoryItem.Subtitles.push(
          StaticClass.createSubtitle(
            this.uniteDateAndTime(
              historyAlarm.solutionDay,
              historyAlarm.solutionTime,
            ),
            "",
            this._warning,
            this._primary,
          ),
        );
      }
      return newAlarmsHistoryItem;
    });
  }

  /**
   * Creates and returns the columns to pass to the table.
   */
  public createArrayColumns(): Column[] {
    let columns = [];
    columns.push(
      StaticClass.createColumnObj(
        this._idTypeAlarmImage,
        this._alarmType,
        this._genericTableLargeCell,
        ColumnTableType.ICON,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._description,
        this._description,
        this._genericTableLargeCell,
        ColumnTableType.STRING,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        "environmentDescription",
        this._environment,
        this._genericTableLargeCell,
        ColumnTableType.STRING,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._day,
        this._date,
        this._genericTableLargeCell,
        ColumnTableType.DATE,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._alarmPeriodHours,
        this._duration,
        "",
        ColumnTableType.STRING,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._solveLaterUserName,
        this._solveLaterUserName,
        this._genericTableLargeCell + " " + this._solveLaterCss,
        ColumnTableType.STRING,
        HideShowColumn.HIDE_1000,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._workingOverNotes,
        this._workingOverNotes,
        this._genericTableLargeCell + " " + this._solveLaterCss,
        ColumnTableType.STRING,
        HideShowColumn.HIDE_1000,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._solutionDay,
        this._solutionDate,
        this._genericTableLargeCell + " " + this._solutionCss,
        ColumnTableType.DATE,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        "userName",
        this._userNameSolution,
        this._genericTableLargeCell + " " + this._solutionCss,
        ColumnTableType.STRING,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._solutionNotes,
        this._solutionNotes,
        this._genericTableLargeCell + " " + this._solutionCss,
        ColumnTableType.STRING,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._resetDay,
        this._resetDate,
        this._genericTableLargeCell + " " + this._resetCss,
        ColumnTableType.DATE,
        HideShowColumn.HIDE_1500,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._resetUserName,
        this._resetUserName,
        this._genericTableLargeCell + " " + this._resetCss,
        ColumnTableType.STRING,
        HideShowColumn.HIDE_1500,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        this._resetNotes,
        this._alarmType,
        this._genericTableLargeCell + " " + this._resetCss,
        ColumnTableType.STRING,
        HideShowColumn.HIDE_1500,
      ),
    );
    return columns;
  }

  /**
   * Creates the table info list items to show in the desktop view, though now changed to show same
   * tableInfo-List for both App view and Destop view.
   */
  public createItemHistoryDesktopList(): void {
    if (!Array.isArray(this._historyAlarms)) {
      this._historyAlarms = [];
    }

    let valuesToChange: ValueToChange[][] = this._historyAlarms.map(
      (historyAlarm, historyAlarmIndex) => {
        const subtitle = StaticClass.createSubtitle(
          this.getTextTypeAlarm(historyAlarm.idTypeAlarmImage) !== ""
            ? this._translateService.instant(
                this._dictionary +
                  this.getTextTypeAlarm(historyAlarm.idTypeAlarmImage),
              )
            : "",
          "",
          StaticClass.GetTypeAlarm(historyAlarm.idTypeAlarmImage),
          this.getColorIcon(historyAlarm),
        );

        const valueToChangeArray: ValueToChange[] = [
          StaticClass.createValueToChange(
            this._idTypeAlarmImage,
            historyAlarmIndex,
            subtitle,
          ),
          StaticClass.createValueToChange(
            this._day,
            historyAlarmIndex,
            this.uniteDateAndTime(historyAlarm.day, historyAlarm.time),
          ),
          StaticClass.createValueToChange(
            this._alarmPeriodHours,
            historyAlarmIndex,
            this.getDurationAlarm(historyAlarm),
          ),
          StaticClass.createValueToChange(
            this._solutionDay,
            historyAlarmIndex,
            this.uniteDateAndTime(
              historyAlarm.solutionDay,
              historyAlarm.solutionTime,
            ),
          ),
          StaticClass.createValueToChange(
            this._resetDay,
            historyAlarmIndex,
            this.uniteDateAndTime(
              historyAlarm.resetDay,
              historyAlarm.resetTime,
            ),
          ),
        ];

        return valueToChangeArray;
      },
    );

    let flattenedValuesToChange: ValueToChange[] = [].concat(...valuesToChange);

    this.tableInfo.List = StaticClass.createArrayForTable(
      this._historyAlarms,
      flattenedValuesToChange,
    );

    const tableInfoListDayArray: Array<string> = this.tableInfo.List.map(
      (tableInfoListItem) => {
        return tableInfoListItem.day;
      },
    );

    this.calculateAlarmsHistoryScroll(tableInfoListDayArray);
  }

  /**
   *  Handle the transformation of the emmitted alarms history filter event to the required format
   * @param listAccessHistoryfilterData: the filtered alarms history filtered data
   */
  public async onClickFilterAlarmsHistory(
    alarmsHistoryfilterData: Map<string, string>,
  ): Promise<void> {
    try {
      
      let queryString: string = "";

      let alarmsHistoryFilteredMapData: Map<string, string> =
        this._dateFormatterService.formatValuesFilter(
          alarmsHistoryfilterData,
          this._lblErrorDate,
          this.tableInfo,
        );
      if (alarmsHistoryFilteredMapData) {
        queryString = Array.from(alarmsHistoryFilteredMapData).reduce(
          (accumulator, [key, value], index) => {
            if (key === "idEnvironment") {
              return accumulator;
            }

            let paramKey = key;
            let paramValue = value;

            if (key === "fromDay") paramKey = "FromDay";
            if (key === "toDay") paramKey = "ToDay";
            if (key === "fromDaySolution") paramKey = "FromDaySolution";
            if (key === "toDaySolution") paramKey = "ToDaySolution";

            if (
              key === "FromDay" ||
              key === "ToDay" ||
              key === "FromDaySolution" ||
              key === "ToDaySolution"
            ) {
              // Convert DD-MM-YYYY HH:mm:ss to YYYY-MM-DD HH:mm
              paramValue = moment(value, "DD-MM-YYYY HH:mm:ss").format(
                "YYYY-MM-DD HH:mm",
              );
            }

            if (accumulator.length > 0) {
              accumulator += "&";
            }

            return (
              accumulator + `${paramKey}=${encodeURIComponent(paramValue)}`
            );
          },
          "",
        );

        if (!queryString.includes("IDArea")) {
          queryString += `&IDArea=${this._idAreaLocal}`;
        }

        const idEnvironment = alarmsHistoryFilteredMapData.get("idEnvironment");
        if (idEnvironment) {
          queryString += `&IDEnvironment=${idEnvironment}`;
          const env = this._environments.find(
            (e) => e.id === Number(idEnvironment),
          );
        }
        const name = alarmsHistoryfilterData.get("name");
        if (name) {
          queryString += `&Name=${encodeURIComponent(name)}`;
        }

        this._currentOffset = 0;
        queryString += `&nRow=${this._numElementInScroll}&offset=${this._currentOffset}`;
        this._currentQuery = queryString;

        await this.onRequestAlarmHistoryFilter(queryString);
      }
    } catch (exception) {
      await this.handlePeculiarApiResponseError(exception);
    }
  }

  /**
   * Reload the alarms history list using the current query string
   */
  public async onReloadAlarmsHistory(): Promise<void> {
    await this.onRequestAlarmHistoryFilter(this._currentQuery);
  }

  /**
   * Format the tableInfo List date and time based on the required format
   * @param dateString: alarms history filtered date as string
   * @param timeString alarms history filtered time as string
   * @returns date string in the required format
   */
  formatDateTime(dateString: string): string {
    const dateTimeString = `${dateString}`;
    const dateFormat = "DD/MM/YY HH:mm:ss";
    const dateRegex = /^\d{2}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2}$/;

    if (
      dateRegex.test(dateString) &&
      moment(dateTimeString, dateFormat).isValid()
    ) {
      return moment(dateTimeString, dateFormat)
        .format("DD MMMM YYYY HH:mm:ss")
        .toString();
    } else {
      return dateTimeString;
    }
  }

  /**
   * Fold / unfold the filter panel (phones and tablets)
   */
  public toggleFilters(): void {
    this.isToggleFilterAlarmsHistory = !this.isToggleFilterAlarmsHistory;
  }

  /**
   * Run the search with the current filters; on small screens the panel folds
   * back so the results are visible
   */
  public async onClickApplyFilters(): Promise<void> {
    const formValues =
      this.filterAlarmsHistoryComponent.filterAlarmsHistoryResultForm.value;
    this.appliedFilters = {
      fromDay: formValues.fromDay || "",
      toDay: formValues.toDay || "",
      environment: formValues.idEnvironment?.Value || "",
      name: formValues.name || "",
    };
    const queryParams =
      this.filterAlarmsHistoryComponent.onFilterAlarmsHistoryList();
    await this.onClickFilterAlarmsHistory(queryParams);
    this.isToggleFilterAlarmsHistory = false;
  }

  /**
   * Restore the default filter values
   */
  public onClickResetFilters(): void {
    this.filterAlarmsHistoryComponent?.resetFilters();
  }

  /**
   * Translation key of the state of an alarm (active, seen, solved, reset)
   */
  public getAlarmStateLabel(tableInfoListItem: any): string {
    return this._dictionary + this.getNameTrad(tableInfoListItem.state);
  }

  /**
   * Get the  icon class of the alarms history list item
   * @param tableInfoListItem: tableInfo List item
   * @returns string> icon class
   */
  public getAlarmsHistoryItemIconClass(tableInfoListItem: any): string {
    switch (tableInfoListItem.state) {
      case BIGLogicLogState.Archived:
        return "dark-background";
      case BIGLogicLogState.Solved:
        return "primary-background";
      case BIGLogicLogState.ToSolveLater:
        return "warning-background";
      case BIGLogicLogState.Inserted:
        return "danger-background";

      default:
        return "default-background";
    }
  }

  /**
   * Get the icon name of the alarms history list item
   * @param tableInfoListItem: tableInfo List item
   * @returns string> icon name
   */
  public getAlarmsHistoryItemIconName(tableInfoListItem: any): string {
    switch (tableInfoListItem.idTypeAlarmImage.IconInfo.Name) {
      case "warning":
        return "warning-outline";
      case "medkit":
        return "medkit-outline";
      case "flame":
        return "flame-outline";
      case "water":
        return "water-outline";
      case "bonfire":
        return "bonfire-outline";
      case "laptop":
        return "laptop-outline";
      default:
        return "alert-circle-outline";
    }
  }

  /**
   * Handles the alarms history infinte scroll
   * @param event IonicInfinite
   */
  public async doInfiniteScroll(event: any): Promise<void> {
    const previousCount = this.alarmsHistoryScroll.length;

    // If there's local data still to be shown (unlikely with _numElementInScroll = 100)
    const scrollStartArray: number =
      this._alarmsHistoryPageScroll * this._numElementInScroll;

    if (scrollStartArray < this.alarmsHistoryFiltered.length) {
      this._alarmsHistoryPageScroll++;
      let scrollEndArray: number =
        this._alarmsHistoryPageScroll * this._numElementInScroll;
      if (scrollEndArray >= this.alarmsHistoryFiltered.length) {
        scrollEndArray = this.alarmsHistoryFiltered.length;
      }

      setTimeout(() => {
        this.alarmsHistoryScroll.push(
          ...this.alarmsHistoryFiltered.slice(scrollStartArray, scrollEndArray)
        );
        this.calculateRemainingAlarmsHistoryItemSearch();
        event.target.complete();
      }, 500);
    } else if (
      this._historyAlarms.length > 0 &&
      this._historyAlarms.length % this._numElementInScroll === 0
    ) {
      // Fetch more from server
      this._currentOffset += this._numElementInScroll;
      let query = this._currentQuery;

      // Update offset in query string
      const offsetRegex = /offset=\d+/;
      if (offsetRegex.test(query)) {
        query = query.replace(offsetRegex, `offset=${this._currentOffset}`);
      } else {
        query += `&offset=${this._currentOffset}`;
      }

      this._currentQuery = query;
      await this.onRequestAlarmHistoryFilter(query, true, true);
      event.target.complete();
    } else {
      this._isLoadingAlarmsHistory = false;
      event.target.complete();
    }
  }

  /**
   * Track by function for the alarms history list to improve performance and prevent DOM recreation
   */
  public trackById(index: number, item: any): any {
    return item.id || index;
  }

  /**
   * Handle the toggling of each alarms history list item
   */
  public onClickExpandCollapseHistoryItem(itemId: number) {
    const index = this.toggledOpenItemIds.indexOf(itemId);
    if (index > -1) {
      this.toggledOpenItemIds.splice(index, 1);
    } else {
      this.toggledOpenItemIds.push(itemId);
    }
  }
  /**
   * Given an alarm, it returns the type assigned to it.
   * @param idType
   */
  private getTextTypeAlarm(idType: number): string {
    switch (idType) {
      case BIGLogicTypeAlarm.FireAlarm:
        return "fireAlarm";
      case BIGLogicTypeAlarm.SupportRequestAlarm:
        return "supportRequestAlarm";
      case BIGLogicTypeAlarm.AntifreezeAlarm:
        return "AntifreezeAlarm";
      case BIGLogicTypeAlarm.RequestForRoomService:
        return "requestForRoomService";
      case BIGLogicTypeAlarm.RiskOfFlooding:
        return "riskOfFlooding";
      case BIGLogicTypeAlarm.TooHotAlarm:
        return "tooHotAlarm";
      case BIGLogicTypeAlarm.TechnicalAlarm:
        return "technicalAlarm";
      default:
        return "";
    }
  }

  /**
   * Given an alarm, it returns the color of the icon assigned to it.
   * @param row alarm
   */
  private getColorIcon(row: HistoryAlarm): string {
    switch (row.state) {
      case BIGLogicLogState.Inserted:
        return this._danger;
      case BIGLogicLogState.ToSolveLater:
        return this._warning;
      case BIGLogicLogState.Solved:
        return this._primary;
      case BIGLogicLogState.Archived:
        return "generic-table-dark";
    }
  }

  /**
   * Given an alarm, if the alarm has been stopped, it returns its duration.
   * @param row alarms
   */
  private getDurationAlarm(row: HistoryAlarm): string {
    let seconds = Number(row.alarmPeriodSeconds);
    let minutes = Number(row.alarmPeriodMinutes);
    let hours = Number(row.alarmPeriodHours);
    let durH = hours > 0 ? hours + "h" : "";
    let durM = minutes > 0 ? minutes + "m" : "";
    let durS = seconds > 0 ? seconds + "s" : "";
    return durH + " " + durM + " " + durS;
  }

  /**
   * Creates the array containing the filters to be used for queries.
   */
  private initFilterFields(): void {
    let currentDate = moment().hour(23).minute(59).second(0).toDate();
    let yesterdayDate = moment()
      .hour(0)
      .minute(0)
      .second(0)
      .subtract(1, "day")
      .toDate();
    this.tableInfo.Filters = [];
    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.text,
        "name",
        this._dictionary + "alarmName",
        "",
        [],
        "",
      ),
    );
    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.date,
        this._fromDay,
        this._dictionary + "start_date",
        "",
        [],
        moment(yesterdayDate).format(
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE,
        ),
      ),
    );
    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.date,
        this._toDay,
        this._dictionary + "end_date",
        "",
        [],
        moment(currentDate).format(
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE,
        ),
      ),
    );

    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.combo,
        "idEnvironment",
        this._dictionary + this._environment,
        "",
        this._environments.map((env) => ({
          Id: env.id,
          Value:
            env.descriptionEnvironment ||
            env.translatedNRoom ||
            env.customDescription ||
            env.nRoom,
        })),
        "",
      ),
    );
    /*this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.date,
        this._fromDaySolution,
        this._dictionary + "startDateSolution",
        "",
        [],
        "",
      ),
    );
    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.date,
        this._toDaySolution,
        this._dictionary + "endDateSolution",
        "",
        [],
        "",
      ),
    );

    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.combo,
        this._logicLogState,
        this._dictionary + "stateCtrl",
        "",
        this.createArrayComboFilter(),
        this._idAll.toString(),
      ),
    );*/
  }

  /**
   * Create the array for the combobox present in the filters.
   */
  /**
   * Create the array for the combobox present in the filters.
   */
  private createArrayComboFilter(): Array<FilterValues> {
    let enumArray = EnumStringConverter.genericEnumtoArray(BIGLogicLogState);
    let selectArray: Array<FilterValues> = Array<FilterValues>();

    selectArray.push({
      Id: this._idAll,
      Value: this._dictionary + this.getNameTrad(this._idAll),
    });

    selectArray = selectArray.concat(
      enumArray.map((enumArrayItem) => ({
        Id: Number(enumArrayItem),
        Value: this._dictionary + this.getNameTrad(Number(enumArrayItem)),
      })),
    );

    return selectArray;
  }

  /**
   * Handles the BIGLogicLogState type check based on the LogicLogState Value
   * @param listAlarmsHistoryValue Alarms history results BIG LogicLogState Value
   * @returns string:  LogicLogState
   */
  private getNameTrad(listAlarmsHistoryValue: number): string {
    switch (listAlarmsHistoryValue) {
      case BIGLogicLogState.Inserted:
        return "attivo_weekly";
      case BIGLogicLogState.ToSolveLater:
        return "toSolveLater";
      case BIGLogicLogState.Solved:
        return "solved";
      case BIGLogicLogState.Archived:
        return "archived";
      default:
        return "all";
    }
  }

  /**
   * Returns the merged date and time.
   * @param date date
   * @param time time
   */
  private uniteDateAndTime(date: string, time: string): string {
    if (date === this._defaultDate || date === "") return "";
    else {
      date = this._translationService.formatDateTime(
        moment(date, "DD/MM/YYYY"),
      );
      let dateSplit = date.split(" ");
      let dateTime: Date = new Date("01/01/0001" + " " + time);
      time = this._translationService.formatDateTime(moment(dateTime));
      let timeSplit = time.split(" ");
      date = dateSplit[0];
      time =
        timeSplit.length > 2 ? timeSplit[1] + " " + timeSplit[2] : timeSplit[1];
      return date + " " + time;
    }
  }

  /**
   * Handle errors in api call which require peculiar reactions from page;
   * this includes cases where the error is propagated through subject.error()
   */
  private async handlePeculiarApiResponseError(error: any): Promise<void> {
    this.tableInfo.ShowErrorContent = true;
    this.tableInfo.IsListInitialized = true;
    switch (error.status) {
      case ApiErrorType.NOT_FOUND:
        this.tableInfo.IsFunctionalityNotImplemented = true;
        break;
      default:
        await this.handleApiResponseError();
        break;
    }
  }

  private async handleApiResponseError(): Promise<void> {
    await this._alertService.presentAlertSomethingWrong();
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;
          if (this.currentConnectionType !== ConnectionType.NONE) {
            if (this._currentQuery !== "") {
              this.tableInfo.IsListInitialized = true;
              this.onRequestAlarmHistoryFilter(this._currentQuery);
            }
          }
        },
      );
  }

  private subscribeBuildingChange(): void {
    this._selectedBuildingSubscription = this._store
      .select(selectedBuilding)
      .subscribe(async (building: Building | any) => {
        if (building && building.idAreaLocal !== this._idAreaLocal) {
          this._idAreaLocal = building.idAreaLocal;
          this.tableInfo.IsListInitialized = false;
          if (this._idAreaLocal !== -1) {
            try {
              await this._loaderService.presentLoaderWaiting();

              await this.fetchEnvironments(this._idAreaLocal, true);

              await this.onRequestAlarmHistoryFilter(
                `IDArea=${this._idAreaLocal}`,
                true,
              );
            } catch (error) {
              this._loggerService.log(
                LogLevel.LOG_ERROR,
                this._logContext,
                this._logContext,
                "subscribeBuildingChange()",
                "Error in building change workflow: " + error,
              );
            } finally {
              await this._loaderService.dismissLoaderWaiting();
            }
          }
        }
      });
  }

  private async fetchEnvironments(
    idArea: number,
    skipLoader: boolean = false,
  ): Promise<void> {
    try {
      if (!skipLoader) {
        await this._loaderService.presentLoaderWaiting();
      }
      const response = await this._environmentService
        .getEnvironments(idArea, null, false, false)
        .toPromise();
      if (response && response.data) {
        this._environments = this.flattenEnvironments(response.data);
        this.initFilterFields();
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "fetchEnvironments()",
        "Error fetching environments: " + error,
      );
      this._environments = [];
      this.initFilterFields();
    } finally {
      if (!skipLoader) {
        await this._loaderService.dismissLoaderWaiting();
      }
    }
  }

  private flattenEnvironments(envs: any[]): Environment[] {
    let flattened: Environment[] = [];
    if (!envs) return flattened;
    envs.forEach((env) => {
      flattened.push(env);
      if (env.children && env.children.length > 0) {
        flattened = flattened.concat(this.flattenEnvironments(env.children));
      }
    });
    return flattened;
  }

  /**
   * Calculates the remaing items in the array of the list alarms history filtered results
   */
  private calculateRemainingAlarmsHistoryItemSearch(): void {
    const loaded: number = this._historyAlarms.length;
    const total: number = Math.max(this._totalAlarmsHistoryCount, loaded);
    const toLoad: number = total - loaded;

    this._translateService
      .get(["dictionary.remainingItemsOther", "dictionary.remainingItems"])
      .subscribe((translations) => {
        const other = translations["dictionary.remainingItemsOther"];
        const remaining = translations["dictionary.remainingItems"];
        this._remainingAccessHistoryItems = `${other} ${toLoad} ${remaining}`;
      });
  }

  /**
   * Calculate the specific alarms history items to display from the list of all filtered alarms history results
   * @param alarmsHistoryFilteredDays
   */
  private calculateAlarmsHistoryScroll(
    alarmsHistoryFilteredDays: string[],
  ): void {
    if (this._currentOffset === 0) {
      this.isLoading = true;
    }
    if (
      alarmsHistoryFilteredDays !== undefined &&
      alarmsHistoryFilteredDays.length >= 4
    ) {
      if (this._currentOffset === 0) {
        this.alarmsHistoryScroll = [];
      }

      this.alarmsHistoryFiltered = this.tableInfo.List.filter(
        (tableInfoListItem) => {
          return alarmsHistoryFilteredDays.some((filteredDay) =>
            tableInfoListItem.day.includes(filteredDay),
          );
        },
      );
    } else {
      this.alarmsHistoryFiltered = this.tableInfo.List;
    }
    this.populateListAccessHistorySearch();
    this.isLoading = false;
  }

  /**
   * Populates the alarmsHistoryScroll based on the result of the calculateAlarmsHistoryScroll
   */
  private populateListAccessHistorySearch(): void {
    // If we're at the beginning (not infinite scroll result), reset the visible list
    if (
      this._currentOffset === 0 &&
      this._historyAlarms.length <= this._numElementInScroll
    ) {
      if (this.alarmsHistoryFiltered.length < this._numElementInScroll) {
        this.alarmsHistoryScroll = this.alarmsHistoryFiltered.slice(0);
        this._isLoadingAlarmsHistory = false;
      } else {
        this.alarmsHistoryScroll = this.alarmsHistoryFiltered.slice(
          0,
          this._numElementInScroll,
        );
        this._isLoadingAlarmsHistory = true;
        this.isScrollVisible = true;
      }
      this._alarmsHistoryPageScroll = 1;
    } else {
      // Append new items to the visible list
      const scrollStartArray =
        this._historyAlarms.length - this._numElementInScroll;
      const newItems = this.alarmsHistoryFiltered.slice(scrollStartArray);
      this.alarmsHistoryScroll.push(...newItems);
      this._isLoadingAlarmsHistory =
        newItems.length === this._numElementInScroll;
      this._alarmsHistoryPageScroll = Math.ceil(
        this.alarmsHistoryFiltered.length / this._numElementInScroll,
      );
    }

    if (this.alarmsHistoryScroll.length === 0) {
      this.isAlarmsHistoryEmpty = true;
      this.isLoading = false;
    } else {
      this.isAlarmsHistoryEmpty = false;
    }
    this.calculateRemainingAlarmsHistoryItemSearch();
  }
}
